import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBooking } from "../../../context/BookingContext";
import {
  getAllStringsFromInventory,
  createPaymentOrBooking,
  getActiveDiscountApi,
} from "../../../api/api";
import styles from "./BookingFinal.module.css";
import termsAndConditions from "../../../constants/termsAndConditions";
import PayInStoreButton from "../../../features/payment/PayInStore/PayInStoreButton";
import useAdminGuard from "../../../hooks/useAuthGuard";
import AddressForm from "../../booking/AddressForm/AddressForm";

function joinAddress(...parts) {
  return parts
    .map((p) => (p ?? "").toString().trim())
    .filter((p) => p && p.toLowerCase() !== "null")
    .join(", ");
}

const readStringId = (b) =>
  b?.stringId ?? b?.string_id ?? b?.selectedStringId ?? null;
const readStringType = (b) => b?.stringType ?? b?.string_type ?? null;

// Utility function to calculate service fee based on pickup service type
const calculateServiceFee = (pickupService) => {
  const serviceFees = {
    pickup: 30,
    delivery: 30,
    "pickup/delivery": 60,
    "express-pickup": 50,
    "express-delivery": 50,
    "express-pickup/delivery": 100,
    "in-store": 0,
  };
  return serviceFees[pickupService] || 0;
};

const BookingFinal = () => {
  useAdminGuard();
  const {
    bookings,
    setBookings,
    getAddressForCustomer,
    setAddressForCustomer,
  } = useBooking();
  const navigate = useNavigate();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = useState("");
  const termsRef = useRef(null);
  const clearErrTimerRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newStringRacketName, setNewStringRacketName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [isBreakdownPopupOpen, setIsBreakdownPopupOpen] = useState(false);
  const redirectTimerRef = useRef(null);
  const [strings, setStrings] = useState([]);

  const {
    customerId,
    services = "Stringing Service",
    bookingDate = "Not Provided",
    freeServices = 0,
    usedServices = 0,
    customerName = "N/A",
    pickupService = "in-store",
    instoreLocation = null,
    rp_id = null,
    claim_id = null,
    customerEmail = null,
  } = location.state || {};

  const [activeDiscount, setActiveDiscount] = useState(null);

  // Fetch strings from inventory and active discount
  useEffect(() => {
    const fetchStringsAndDiscount = async () => {
      try {
        const [data, discountRes] = await Promise.all([
          getAllStringsFromInventory(),
          getActiveDiscountApi()
        ]);
        setStrings(data);
        if (discountRes.data && discountRes.data.active) {
          setActiveDiscount(discountRes.data.discount);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchStringsAndDiscount();
  }, []);

  const filteredBookings = bookings.filter((b) => b.customerId === customerId);

  const lockedStringType = useMemo(() => {
    const b = filteredBookings.find((x) => readStringType(x));
    return readStringType(b) || null;
  }, [filteredBookings]);

  // Calculate original (undiscounted) individual cost for a booking
  const getOriginalIndividualCost = useCallback(
    (b) => {
      const stringDetail = strings.find(
        (item) => item.string_id === readStringId(b)
      );
      if (!stringDetail) return 0;
      const isExpress = readStringType(b) === "Express";

      if (b.isHybrid) {
        const stringDetailCross = strings.find(
          (item) => item.string_id === b.stringIdCross
        );
        const isMainByos = stringDetail ? (stringDetail.name === "Bring Own String" || (stringDetail.name || "").toLowerCase().includes("bring own")) : true;
        const isCrossByos = stringDetailCross ? (stringDetailCross.name === "Bring Own String" || (stringDetailCross.name || "").toLowerCase().includes("bring own")) : true;
        const bothByos = isMainByos && isCrossByos;

        if (bothByos) {
          return isExpress ? 100 : 75;
        }

        // Consolidated hybrid pricing: 200 regular / 225 express
        return isExpress ? 225 : 200;
      }

      return isExpress ? stringDetail.basePrice + 25 : stringDetail.basePrice;
    },
    [strings]
  );

  // Calculate individual cost for a booking (with active discount applied)
  const getIndividualCost = useCallback(
    (b) => {
      const stringDetail = strings.find(
        (item) => item.string_id === readStringId(b)
      );
      if (!stringDetail) return 0;
      const isExpress = readStringType(b) === "Express";

      if (b.isHybrid) {
        const stringDetailCross = strings.find(
          (item) => item.string_id === b.stringIdCross
        );
        const isMainByos = stringDetail ? (stringDetail.name === "Bring Own String" || (stringDetail.name || "").toLowerCase().includes("bring own")) : true;
        const isCrossByos = stringDetailCross ? (stringDetailCross.name === "Bring Own String" || (stringDetailCross.name || "").toLowerCase().includes("bring own")) : true;
        const bothByos = isMainByos && isCrossByos;

        if (bothByos) {
          if (b.freeService) {
            return isExpress ? 25 : 0;
          }
          if (activeDiscount) {
            return isExpress ? Number(activeDiscount.express_price) : Number(activeDiscount.regular_price);
          }
          return isExpress ? 100 : 75;
        }

        // Consolidated hybrid pricing: 200 regular / 225 express
        if (b.freeService) {
          return isExpress ? 150 : 125;
        }
        if (activeDiscount) {
          const regDiscount = 75 - Number(activeDiscount.regular_price);
          const expDiscount = 100 - Number(activeDiscount.express_price);
          return isExpress ? Math.max(225 - expDiscount, 0) : Math.max(200 - regDiscount, 0);
        }
        return isExpress ? 225 : 200;
      }

      if (b.freeService) {
        return isExpress
          ? Math.max(stringDetail.basePrice - 75 + 25, 0)
          : Math.max(stringDetail.basePrice - 75, 0);
      }
      if (activeDiscount) {
        return isExpress
          ? Math.max(stringDetail.basePrice - 75 + Number(activeDiscount.express_price), 0)
          : Math.max(stringDetail.basePrice - 75 + Number(activeDiscount.regular_price), 0);
      }
      return isExpress ? stringDetail.basePrice + 25 : stringDetail.basePrice;
    },
    [strings, activeDiscount]
  );

  const originalStringCost = useMemo(() => {
    return filteredBookings.reduce((acc, b) => acc + getOriginalIndividualCost(b), 0);
  }, [filteredBookings, getOriginalIndividualCost]);

  const discountDetails = useMemo(() => {
    if (!activeDiscount) return null;
    let regularCount = 0;
    let expressCount = 0;
    let totalSaved = 0;

    const regDiscount = 75 - Number(activeDiscount.regular_price);
    const expDiscount = 100 - Number(activeDiscount.express_price);

    filteredBookings.forEach((b) => {
      if (b.freeService) return;
      const isExpress = readStringType(b) === "Express";
      if (isExpress) {
        expressCount += 1;
        totalSaved += expDiscount;
      } else {
        regularCount += 1;
        totalSaved += regDiscount;
      }
    });

    return {
      name: activeDiscount.discount_name,
      regularCount,
      expressCount,
      regularDiscount: regDiscount,
      expressDiscount: expDiscount,
      totalSaved,
    };
  }, [filteredBookings, activeDiscount]);

  const breakdown = useMemo(() => {
    const stringCost = filteredBookings.reduce(
      (acc, b) => acc + getIndividualCost(b),
      0
    );

    const pickupFee = calculateServiceFee(pickupService);

    return {
      stringCost,
      pickupFee,
      grandTotal: stringCost + pickupFee,
    };
  }, [filteredBookings, pickupService, getIndividualCost]);

  const overallTotal = breakdown.grandTotal;

  const formatTension = (booking) => {
    if (!booking.tensionType) return "—";
    if (booking.tensionType === "support") return "Talk with support";
    if (booking.isHybrid) {
      if (booking.tensionType === "standard") return "Main: Standard, Cross: Standard";
      const unit = booking.tensionUnit || "lbs";
      const mainPrestretch = booking.prestretch ? ` (Prestretch: ${booking.prestretchPercent ?? "—"}%)` : "";
      const crossPrestretch = booking.prestretchCross ? ` (Prestretch: ${booking.prestretchPercentCross ?? "—"}%)` : "";
      const mainStr = booking.tensionMain != null
        ? `${booking.tensionMain} ${unit}${mainPrestretch}`
        : (booking.tensionOptionMain === "support" ? "Talk with support" : "Standard");
      const crossStr = booking.tensionCross != null
        ? `${booking.tensionCross} ${unit}${crossPrestretch}`
        : (booking.tensionOptionCross === "support" ? "Talk with support" : "Standard");
      return `Main: ${mainStr}, Cross: ${crossStr}`;
    }
    if (booking.tensionType === "standard") return "Standard";
    if (booking.tensionType === "custom") {
      const unit = booking.tensionUnit || "lbs";
      let label = `${booking.tensionMain ?? "—"}${unit} main / ${booking.tensionCross ?? "—"}${unit} cross`;
      if (booking.prestretch) {
        label += `, Prestretch: ${booking.prestretchPercent ?? "—"}%`;
      }
      return label;
    }
    return "—";
  };

  useEffect(() => {
    return () => {
      const id = redirectTimerRef.current;
      if (id) clearTimeout(id);
      if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
    };
  }, []);

  const showError = (msg, { focusTerms = false } = {}) => {
    setErrorMessage(msg);
    if (focusTerms) {
      setTermsError(true);
    }
    if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
    clearErrTimerRef.current = setTimeout(() => setErrorMessage(""), 3500);
    if (focusTerms && termsRef.current) {
      termsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      termsRef.current.classList.add(styles.pulseHighlight);
      termsRef.current.classList.add("pulseHighlight");
      setTimeout(() => {
        termsRef.current?.classList.remove(styles.pulseHighlight);
        termsRef.current?.classList.remove("pulseHighlight");
      }, 1600);
    }
  };

  const handleDelete = (index) => {
    const bookingToDelete = filteredBookings[index];
    const updatedBookings = bookings.filter((b) => b !== bookingToDelete);
    setBookings(updatedBookings);
    localStorage.setItem("bookings", JSON.stringify(updatedBookings));
  };

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBookingIndex, setSelectedBookingIndex] = useState(null);
  const [selectedStringId, setSelectedStringId] = useState(
    location.state?.string_id || null
  );
  const [selectedStringType, setSelectedStringType] = useState(
    location.state?.string_type || ""
  );
  const [totalCost, setTotalCost] = useState("");
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isTensionModalOpen, setIsTensionModalOpen] = useState(false);
  const [tensionOption, setTensionOption] = useState("");
  const [tensionOptionMain, setTensionOptionMain] = useState("");
  const [tensionOptionCross, setTensionOptionCross] = useState("");
  const [tensionModalTarget, setTensionModalTarget] = useState("main");
  const [tensionMain, setTensionMain] = useState("");
  const [tensionCross, setTensionCross] = useState("");
  const [tensionUnit, setTensionUnit] = useState("lbs");
  const [prestretchEnabled, setPrestretchEnabled] = useState(false);
  const [prestretchPercent, setPrestretchPercent] = useState("");
  const [selectedStringIdCross, setSelectedStringIdCross] = useState(null);
  const [prestretchEnabledCross, setPrestretchEnabledCross] = useState(false);
  const [prestretchPercentCross, setPrestretchPercentCross] = useState("");
  const [hybridTab, setHybridTab] = useState("main");

  const handleEdit = (index) => {
    const bookingToEdit = filteredBookings[index];
    if (!bookingToEdit) return;
    setSelectedBookingIndex(index);

    setSelectedStringId(readStringId(bookingToEdit) || null);
    setSelectedStringIdCross(bookingToEdit.stringIdCross || null);
    setSelectedStringType(readStringType(bookingToEdit) || "");
    setTotalCost(getIndividualCost(bookingToEdit) || 0);

    const optMain = bookingToEdit.tensionOptionMain || (bookingToEdit.tensionMain != null ? "custom" : (bookingToEdit.tensionType === "standard" ? "standard" : (bookingToEdit.tensionType === "support" ? "support" : "standard")));
    const optCross = bookingToEdit.tensionOptionCross || (bookingToEdit.tensionCross != null ? "custom" : (bookingToEdit.tensionType === "standard" ? "standard" : (bookingToEdit.tensionType === "support" ? "support" : "standard")));
    setTensionOptionMain(optMain);
    setTensionOptionCross(optCross);
    setTensionModalTarget("main");

    setTensionOption(bookingToEdit.tensionType || "");
    setTensionMain(bookingToEdit.tensionMain != null ? String(bookingToEdit.tensionMain) : "");
    setTensionCross(bookingToEdit.tensionCross != null ? String(bookingToEdit.tensionCross) : "");
    setTensionUnit(bookingToEdit.tensionUnit || "lbs");
    setPrestretchEnabled(!!bookingToEdit.prestretch);
    setPrestretchPercent(bookingToEdit.prestretchPercent != null ? String(bookingToEdit.prestretchPercent) : "");
    setPrestretchEnabledCross(!!bookingToEdit.prestretchCross);
    setPrestretchPercentCross(bookingToEdit.prestretchPercentCross != null ? String(bookingToEdit.prestretchPercentCross) : "");
    setHybridTab("main");

    setEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (selectedBookingIndex === null) return;
    
    const bookingToEdit = filteredBookings[selectedBookingIndex];
    if (!bookingToEdit) return;
    const isHybrid = !!bookingToEdit.isHybrid;

    if (isHybrid) {
      if (tensionOptionMain === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          showError("Please enter a valid main tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          showError("Please enter a valid main prestretch percentage.");
          return;
        }
      }
      if (tensionOptionCross === "custom") {
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          showError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabledCross && (!prestretchPercentCross || parseFloat(prestretchPercentCross) <= 0)) {
          showError("Please enter a valid cross prestretch percentage.");
          return;
        }
      }
    } else {
      if (tensionOption === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          showError("Please enter a valid main tension value.");
          return;
        }
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          showError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          showError("Please enter a valid main prestretch percentage.");
          return;
        }
      }
    }
    
    if (isHybrid && (!selectedStringId || !selectedStringIdCross)) {
      showError("Please select both a main and cross string for hybrid setup.");
      return;
    }

    const effectiveTensionType = isHybrid
      ? (tensionOptionMain === "standard" && tensionOptionCross === "standard"
          ? "standard"
          : (tensionOptionMain === "support" && tensionOptionCross === "support"
              ? "support"
              : "custom"))
      : tensionOption;

    let updatedBookings = bookings.slice();
    updatedBookings = updatedBookings.map((b) => {
      if (b !== bookingToEdit) return b;
      if (!selectedStringId || !selectedStringType) return b;
      const stringDetail = strings.find(
        (s) => s.string_id === selectedStringId
      );
      if (!stringDetail) return b;

      const updated = {
        ...b,
        stringId: selectedStringId,
        stringType: selectedStringType,
        stringName: stringDetail.name,
        stringBrand: stringDetail.brand,
        stringIdCross: isHybrid ? selectedStringIdCross : null,
        tensionType: effectiveTensionType,
        tensionOptionMain: isHybrid ? tensionOptionMain : null,
        tensionOptionCross: isHybrid ? tensionOptionCross : null,
        tensionMain: (isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom") ? parseFloat(tensionMain) : null,
        tensionCross: (isHybrid ? tensionOptionCross === "custom" : tensionOption === "custom") ? parseFloat(tensionCross) : null,
        tensionUnit: (isHybrid ? (tensionOptionMain === "custom" || tensionOptionCross === "custom") : tensionOption === "custom") ? tensionUnit : null,
        prestretch: (isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom") && prestretchEnabled ? 1 : 0,
        prestretchPercent: (isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom") && prestretchEnabled && prestretchPercent ? parseFloat(prestretchPercent) : null,
        prestretchCross: isHybrid && tensionOptionCross === "custom" && prestretchEnabledCross ? 1 : 0,
        prestretchPercentCross: isHybrid && tensionOptionCross === "custom" && prestretchEnabledCross && prestretchPercentCross ? parseFloat(prestretchPercentCross) : null,
      };

      const finalCost = getIndividualCost(updated);
      updated.stringPrice = finalCost;
      updated.totalCost = finalCost;
      return updated;
    });

    setBookings(updatedBookings);
    localStorage.setItem("bookings", JSON.stringify(updatedBookings));
    setEditModalOpen(false);
  };

  const handleAddMore = () => {
    if (!newStringRacketName.trim()) {
      showError("Please enter a racket name before adding more.");
      return;
    }
    navigate("/dashboard/booking-form", {
      state: {
        customerId,
        customerName,
        bookingDate,
        pickupService,
        instoreLocation,
        services,
        racketName: newStringRacketName,
        freeServices,
        usedServices,
        isFree: false,
        stringTypeConstraint: lockedStringType,
        isAddMore: true,
        lockServiceSelection: true,
      },
    });
  };

  const generateUniqueCartId = () =>
    `TXN-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const effectiveAddress = useMemo(
    () => (getAddressForCustomer ? getAddressForCustomer(customerId) : ""),
    [getAddressForCustomer, customerId]
  );

  const handleProceedToPay = async () => {
    if (!agreedToTerms) {
      showError("Please agree to the Terms & Conditions before proceeding.", {
        focusTerms: true,
      });
      return;
    }
    if (filteredBookings.length === 0) {
      showError("No bookings to process.");
      return;
    }

    if (pickupService !== "in-store" && !effectiveAddress?.trim()) {
      console.warn(
        `[BookingFinal] Blocked proceed to pay — pickup service "${pickupService}" selected but no delivery address provided. Customer: ${customerId}, bookings: ${filteredBookings.length}`
      );
      showError("Please add a delivery address before proceeding.");
      return;
    }

    const normalized = filteredBookings.map((b) => ({
      ...b,
      serviceKind: "string",
      stringId: readStringId(b),
      stringType: readStringType(b),
    }));

    for (const b of normalized) {
      if (!b.stringId)
        return showError("Please select a string for all string bookings.");
      if (!b.stringType)
        return showError(
          "Please select Regular/Express for all string bookings."
        );
    }

    setIsProcessing(true);

    const cartId = generateUniqueCartId();
    const bookingRequests = normalized.map((b) => {
      const baseRequest = {
        bigcommerce_customer_id: customerId,
        service_kind: b.serviceKind || "string",
        string_id: b.serviceKind === "string" ? b.stringId : null,
        grip_id: b.serviceKind === "grip" ? b.gripId : null,
        booking_date: bookingDate,
        pickup_option: pickupService,
        delivery_address:
          pickupService === "in-store" ? null : effectiveAddress || null,
        instore_location: pickupService === "in-store" ? instoreLocation : null,
        racket_name: b.racketName,
        total_cost: getIndividualCost(b),
        cart_id: cartId,
        freeService: b.freeService || false,
        rp_id: b.rp_id || rp_id || null,
        claim_id: b.claim_id || claim_id || null,
        customer_name: customerName,
        customer_email: customerEmail || null,
        tension_type: b.tensionType || null,
        tension_main: b.tensionType === "custom" && b.tensionMain != null ? parseFloat(b.tensionMain) : null,
        tension_cross: b.tensionType === "custom" && b.tensionCross != null ? parseFloat(b.tensionCross) : null,
        tension_unit: b.tensionType === "custom" ? (b.tensionUnit || null) : null,
        prestretch: b.tensionType === "custom" && b.prestretch ? 1 : 0,
        prestretch_percent: b.tensionType === "custom" && b.prestretch && b.prestretchPercent != null ? parseFloat(b.prestretchPercent) : null,
      };

      // Add string details
      if (b.stringId) {
        const stringDetail = strings.find((s) => s.string_id === b.stringId);
        if (stringDetail) {
          const serviceSpeed = b.stringType || "Regular";
          
          if (b.isHybrid) {
            const stringCross = strings.find((s) => s.string_id === b.stringIdCross);
            const finalCost = getIndividualCost(b);
            
            baseRequest.string_name = stringDetail.name;
            baseRequest.string_brand = stringDetail.brand;
            baseRequest.string_price = 0;
            
            baseRequest.is_hybrid = 1;
            baseRequest.string_id_cross = b.stringIdCross;
            baseRequest.string_name_cross = stringCross ? stringCross.name : null;
            baseRequest.string_brand_cross = stringCross ? stringCross.brand : null;
            baseRequest.string_price_cross = 0;
            
            baseRequest.prestretch_cross = b.prestretchCross ? 1 : 0;
            baseRequest.prestretch_percent_cross = b.prestretchCross && b.prestretchPercentCross != null ? parseFloat(b.prestretchPercentCross) : null;
            
            baseRequest.service_speed = serviceSpeed;
            baseRequest.service_fee = 0;
            baseRequest.total_cost = finalCost;
          } else {
            const stringPrice = getIndividualCost(b);
            baseRequest.string_name = stringDetail.name;
            baseRequest.string_brand = stringDetail.brand;
            baseRequest.string_price = stringPrice;
            baseRequest.service_speed = serviceSpeed;
            baseRequest.service_fee = 0; // Service fee is added once for entire order
            baseRequest.total_cost = stringPrice;
            baseRequest.is_hybrid = 0;
          }
        }
      }

      return baseRequest;
    });
    console.log("payload → /api/payments", {
      bookings: bookingRequests,
      pickup_fee: breakdown?.pickupFee || 0,
    });
    try {
      const response = await createPaymentOrBooking({
        bookings: bookingRequests,
        pickup_fee: breakdown?.pickupFee || 0,
      });
      const data = response.data;

      if (response.status === 200 || response.status === 201) {
        if (data.bookings && data.bookings.length > 0) {
          alert("Booking(s) created successfully!");
          setBookings([]);
          localStorage.removeItem("bookings");

          navigate("/payment-success?freeBooking=1", {
            state: {
              bookingIds: data.bookings.map((b) => b.booking_id),
              customerName,
              totalCost: 0,
              message: "Your booking has been successfully created.",
            },
          });
        } else if (data.payment_url) {
          if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
          redirectTimerRef.current = window.setTimeout(() => {
            window.location.assign(data.payment_url);
          }, 3000);
        } else {
          showError("Something went wrong!");
        }
      } else {
        showError("Failed to create booking or process payment.");
      }
    } catch (error) {
      console.error("Booking Submission Error:", error);
      showError("Error processing bookings.");
    } finally {
      setIsProcessing(false);
    }
  };

  const openBreakdown = () => setIsBreakdownPopupOpen(true);

  return (
    <div className={styles.container}>
      {isProcessing && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.loader}></div>
            <p>Please wait...</p>
            <p className="subText">Your booking is being processed.</p>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <h2 className={styles.title}>Booking Summary</h2>
      <p>
        <strong>Customer Name:</strong> {customerName || "N/A"}
      </p>
      <p>
        <strong>Booking Date:</strong> {bookingDate}
      </p>
      <p>
        <strong>Pickup Service:</strong>{" "}
        {(() => {
          switch (pickupService) {
            case "pickup":
              return "Pickup Only";
            case "delivery":
              return "Delivery Only";
            case "pickup/delivery":
              return "Both Pickup & Delivery";
            case "in-store":
              return instoreLocation ? `In-Store @ ${instoreLocation}` : "In-Store (No Charge)";
            case "express-pickup":
              return "Express Pickup";
            case "express-delivery":
              return "Express Delivery";
            case "express-pickup/delivery":
              return "Express Pickup & Delivery";
            default:
              return pickupService;
          }
        })()}
      </p>
      {pickupService !== "in-store" && (
        <div className={styles.addressSection}>
          <p>
            <strong>Selected Address:</strong>{" "}
            {effectiveAddress || "Not Provided"}
          </p>
          <button
            className={styles.editAddressButton}
            onClick={() => setIsAddressModalOpen(true)}
          >
            {effectiveAddress ? "Change Address" : "Add Address"}
          </button>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Racket Name</th>
              <th>Item Name</th>
              <th>Service Type</th>
              <th>String Tension</th>
              <th>Item Cost</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length > 0 ? (
              filteredBookings.map((booking, index) => {
                const stringDetails = readStringId(booking)
                  ? strings.find((s) => s.string_id === readStringId(booking))
                  : null;

                return (
                  <tr key={index}>
                    <td>{booking.racketName || "Not Provided"}</td>
                    <td>
                      {booking.isHybrid ? (
                        <>
                          <strong>Main:</strong> {stringDetails?.name || "BYOS"}{stringDetails?.brand ? ` (${stringDetails.brand})` : ""}<br/>
                          <strong>Cross:</strong> {(() => {
                            const crossItem = strings.find(s => s.string_id === booking.stringIdCross);
                            return `${crossItem?.name || "BYOS"}${crossItem?.brand ? ` (${crossItem.brand})` : ""}`;
                          })()}
                        </>
                      ) : (
                        `${stringDetails?.name || "Unknown String"}${stringDetails?.brand ? ` (${stringDetails.brand})` : ""}`
                      )}
                    </td>
                    <td>{readStringType(booking) || "Not Selected"}</td>
                    <td>{formatTension(booking)}</td>
                    <td>
                      <span>
                        {booking.freeService ? (
                          <>
                            <span style={{ textDecoration: "line-through", color: "#888", marginRight: "8px" }}>
                              AED {getOriginalIndividualCost(booking)}
                            </span>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {getIndividualCost(booking)}
                            <div style={{ fontSize: "0.78rem", color: "#16a34a", fontWeight: "600" }}>
                              Free Service (-75 AED)
                            </div>
                          </>
                        ) : activeDiscount ? (
                          <>
                            <span style={{ textDecoration: "line-through", color: "#888", marginRight: "8px" }}>
                              AED {getOriginalIndividualCost(booking)}
                            </span>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {getIndividualCost(booking)}
                          </>
                        ) : (
                          <>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {getIndividualCost(booking)}
                          </>
                        )}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEdit(index)}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(index)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className={styles.emptyMessage}>
                  No bookings created yet.
                </td>
              </tr>
            )}
            {filteredBookings.length > 0 && breakdown.pickupFee > 0 && (
              <tr className={styles.serviceFeeRow}>
                <td
                  colSpan="4"
                  style={{ textAlign: "right", fontWeight: "bold" }}
                >
                  Service Charge (
                  {(() => {
                    switch (pickupService) {
                      case "pickup":
                        return "Pickup";
                      case "delivery":
                        return "Delivery";
                      case "pickup/delivery":
                        return "Pickup & Delivery";
                      case "express-pickup":
                        return "Express Pickup";
                      case "express-delivery":
                        return "Express Delivery";
                      case "express-pickup/delivery":
                        return "Express Pickup & Delivery";
                      default:
                        return pickupService;
                    }
                  })()}
                  ):
                </td>
                <td style={{ fontWeight: "bold" }}>
                  <span className="dirham-symbol">&#xea;</span>{" "}
                  {breakdown.pickupFee}
                </td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.overallCostBlock}>
        <span className={styles.overallCostText}>
          <strong>Overall Cost:</strong>{" "}
          <span className="dirham-symbol">&#xea;</span> {overallTotal}
        </span>
        <button className={styles.infoButton} onClick={openBreakdown}>
          ℹ
        </button>
      </div>

      {discountDetails && discountDetails.totalSaved > 0 && (
        <div style={{
          backgroundColor: "#d4edda",
          borderColor: "#c3e6cb",
          color: "#155724",
          padding: "0.75rem 1.25rem",
          borderRadius: "8px",
          marginTop: "0.5rem",
          marginBottom: "1rem",
          fontWeight: "600",
          fontSize: "0.95rem",
          textAlign: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
        }}>
          🎁 Applied <strong>{discountDetails.name}</strong>: Saved <span className="dirham-symbol">&#xea;</span> {discountDetails.totalSaved}!
        </div>
      )}

      {isBreakdownPopupOpen && breakdown && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <button
              type="button"
              className={styles.closeModalButton}
              onClick={() => setIsBreakdownPopupOpen(false)}
            >
              ✖
            </button>
            <h3>Cost Breakdown</h3>
            <p>
              <strong>Subtotal string cost:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {originalStringCost}
            </p>
            {filteredBookings.some((b) => b.freeService) && (
              <p style={{ color: "green", fontWeight: "bold" }}>
                <strong>Free String Service:</strong>{" "}
                -<span className="dirham-symbol">&#xea;</span>{" "}
                {filteredBookings.filter((b) => b.freeService).length * 75}
              </p>
            )}
            {discountDetails && discountDetails.totalSaved > 0 && (
              <p style={{ color: "green", fontWeight: "bold" }}>
                <strong>{discountDetails.name}:</strong>{" "}
                -<span className="dirham-symbol">&#xea;</span>{" "}
                {discountDetails.totalSaved}
              </p>
            )}
            <p>
              <strong>Total String Cost:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.stringCost}
            </p>
            {breakdown.pickupFee > 0 && (
              <p>
                <strong>
                  {(() => {
                    switch (pickupService) {
                      case "pickup":
                        return "Pickup Fee";
                      case "delivery":
                        return "Delivery Fee";
                      case "pickup/delivery":
                        return "Pickup & Delivery Fee";
                      case "express-pickup":
                        return "Express Pickup Fee";
                      case "express-delivery":
                        return "Express Delivery Fee";
                      case "express-pickup/delivery":
                        return "Express Pickup & Delivery Fee";
                      default:
                        return "Service Fee";
                    }
                  })()}
                  :
                </strong>{" "}
                <span className="dirham-symbol">&#xea;</span>{" "}
                {breakdown.pickupFee}
              </p>
            )}
            <hr />
            <p>
              <strong>Grand Total:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.grandTotal}
            </p>
          </div>
        </div>
      )}

      {filteredBookings.length > 0 &&
        !filteredBookings.some((b) => b.freeService === true) && (
          <div className={styles.formGroup}>
            <input
              type="text"
              value={newStringRacketName}
              onChange={(e) => setNewStringRacketName(e.target.value)}
              className={styles.input}
              placeholder="Enter Racket Name"
            />
            <button className={styles.addMoreButton} onClick={handleAddMore}>
              Add More
            </button>
          </div>
        )}

      {/* {filteredBookings.length > 0 && (
        <div className={styles.formGroup}>
          <input
            type="text"
            value={newGripRacketName}
            onChange={(e) => setNewGripRacketName(e.target.value)}
            className={styles.input}
            placeholder="Enter Racket Name (for grip)"
          />
          <button
            className={styles.addMoreButton}
            onClick={() => {
              if (!newGripRacketName.trim()) {
                showError("Please enter a racket name before adding grip.");
                return;
              }
              navigate("/dashboard/grip-booking-form", {
                state: {
                  fromFinal: true,
                  returnPath: "/dashboard/booking-final",
                  customerId,
                  customerName,
                  bookingDate,
                  pickupService,
                  instoreLocation,
                  racketName: newGripRacketName,
                  isAddMore: true,
                  previousBookings: filteredBookings,
                  gripTypeConstraint: (() => {
                    const firstGrip = filteredBookings.find(
                      (b) => b.serviceKind === "grip" && readGripType(b)
                    );
                    return firstGrip?.gripType || null;
                  })(),
                },
              });
            }}
          >
            Add Grip
          </button>
        </div>
      )} */}

      <div
        id="terms-section"
        className={`${styles.termsContainer} ${
          termsError && !agreedToTerms ? styles.termsContainerError : ""
        }`}
        ref={termsRef}
      >
        <label
          className={`${styles.termsLabel} ${
            termsError && !agreedToTerms ? styles.termsLabelError : ""
          }`}
        >
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => {
              const checked = e.target.checked;
              setAgreedToTerms(checked);
              if (checked) {
                setTermsError(false);
                setErrorMessage("");
              }
            }}
          />
          <span>
            I agree to the{" "}
            <button
              type="button"
              onClick={() => setShowTermsPopup(true)}
              className={styles.termsLink}
            >
              Terms & Conditions
            </button>
          </span>
        </label>
        {termsError && !agreedToTerms && (
          <div className={styles.termsErrorInline}>
            Please check the Terms & Conditions box before proceeding.
          </div>
        )}
      </div>

      {showTermsPopup && (
        <div className={styles.termsModal}>
          <div className={styles.termsModalContent}>
            <div dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
            <div className={styles.termsModalFooter}>
              <button
                className={styles.termsCloseButton}
                onClick={() => setShowTermsPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.buttonContainer}>
        <button className={styles.cancelButton} onClick={() => navigate(-1)}>
          Cancel
        </button>
        <button className={styles.proceedButton} onClick={handleProceedToPay}>
          {overallTotal === 0 ? "Confirm Booking" : "Proceed to Pay"}
        </button>

        {overallTotal > 0 && (
          <PayInStoreButton
            bookings={filteredBookings}
            customerId={customerId}
            customerName={customerName}
            pickupService={pickupService}
            address={effectiveAddress}
            bookingDate={bookingDate}
            instoreLocation={instoreLocation}
            agreedToTerms={agreedToTerms}
            strings={strings}
            onError={showError}
          />
        )}
      </div>

      {editModalOpen && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            {(() => {
              const currentBooking = filteredBookings[selectedBookingIndex];
              const isHybrid = !!currentBooking?.isHybrid;
              const lockType =
                currentBooking?.stringType ||
                lockedStringType ||
                "Regular";

              return (
                <>
                  <h3 style={{ marginBottom: isHybrid ? "12px" : "18px" }}>
                    {isHybrid ? "Edit Hybrid Strings" : "Select a String"} (Type Locked: {lockType})
                  </h3>

                  {isHybrid && (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      marginBottom: "16px"
                    }}>
                      <button
                        type="button"
                        onClick={() => setHybridTab("main")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: hybridTab === "main" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "main" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                          <span style={{ fontWeight: "700", fontSize: "0.95rem", color: hybridTab === "main" ? "#0b7b43" : "#374151" }}>
                            1. Main String
                          </span>
                          {selectedStringId ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#e5e7eb", color: "#6b7280", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px" }}>
                              Required
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.85rem", color: selectedStringId ? "#111827" : "#9ca3af", marginTop: "4px", fontWeight: selectedStringId ? "600" : "400" }}>
                          {strings.find((s) => s.string_id === selectedStringId)?.name || "Select Main String"}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setHybridTab("cross")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: hybridTab === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "cross" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                          <span style={{ fontWeight: "700", fontSize: "0.95rem", color: hybridTab === "cross" ? "#0b7b43" : "#374151" }}>
                            2. Cross String
                          </span>
                          {selectedStringIdCross ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#e5e7eb", color: "#6b7280", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px" }}>
                              Required
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.85rem", color: selectedStringIdCross ? "#111827" : "#9ca3af", marginTop: "4px", fontWeight: selectedStringIdCross ? "600" : "400" }}>
                          {strings.find((s) => s.string_id === selectedStringIdCross)?.name || "Select Cross String"}
                        </span>
                      </button>
                    </div>
                  )}

                  {isHybrid && (
                    <div style={{ marginBottom: "10px", fontWeight: "600", color: "#0b7b43", fontSize: "0.95rem" }}>
                      Selecting: {hybridTab === "main" ? "1. Main String" : "2. Cross String"}
                    </div>
                  )}

                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Item Name</th>
                        <th>Type</th>
                        <th>Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {strings.map((stringItem) => {
                        // Skip if out of stock
                        if (!stringItem.availableInAnyStore) {
                          return null;
                        }

                        const isExpress = lockType === "Express";
                        const idForRow = stringItem.string_id;
                        const costForRow = isExpress
                          ? stringItem.basePrice + 25
                          : stringItem.basePrice;
                        const isSelected = isHybrid
                          ? (hybridTab === "main" ? selectedStringId === idForRow : selectedStringIdCross === idForRow)
                          : selectedStringId === idForRow;

                        const cdnBase = process.env.REACT_APP_CDN_URL || "https://cdn11.bigcommerce.com/s-meuoh09dlj";
                        const imageUrl = stringItem.image_url
                          ? (stringItem.image_url.startsWith("http") ? stringItem.image_url : `${cdnBase}${stringItem.image_url}`)
                          : "/images/strings/default.jpg";

                        return (
                          <tr
                            key={stringItem.string_id}
                            className={isSelected ? styles.selectedRow : ""}
                            onClick={() => {
                              if (isHybrid) {
                                if (hybridTab === "main") {
                                  setSelectedStringId(idForRow);
                                  setSelectedStringType(lockType);
                                  const temp = {
                                    ...currentBooking,
                                    stringId: idForRow,
                                    stringIdCross: selectedStringIdCross,
                                    stringType: lockType,
                                  };
                                  setTotalCost(getIndividualCost(temp));
                                } else {
                                  setSelectedStringIdCross(idForRow);
                                  setSelectedStringType(lockType);
                                  const temp = {
                                    ...currentBooking,
                                    stringId: selectedStringId,
                                    stringIdCross: idForRow,
                                    stringType: lockType,
                                  };
                                  setTotalCost(getIndividualCost(temp));
                                }
                              } else {
                                setSelectedStringId(idForRow);
                                setSelectedStringType(lockType);
                                setTotalCost(costForRow);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <img
                                src={imageUrl}
                                alt={stringItem.name}
                                className={styles.stringImageSmall}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/images/strings/default.jpg";
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="radio"
                                name="selectedString"
                                value={idForRow}
                                onChange={() => {}}
                                checked={isSelected}
                              />
                              {" "}{stringItem.name} ({stringItem.brand})
                              {!isHybrid && (
                                <>
                                  {" "}(+ <span className="dirham-symbol">&#xea;</span> {costForRow})
                                </>
                              )}
                            </td>
                            <td>
                              <span className={styles.badge}>{lockType}</span>
                            </td>
                            <td>
                              {isSelected ? (
                                <span>
                                  <span className="dirham-symbol">&#xea;</span>{" "}
                                  {totalCost}
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Tension Button */}
                  <div style={{ marginTop: "16px", marginBottom: "8px" }}>
                    <button
                      type="button"
                      onClick={() => setIsTensionModalOpen(true)}
                      className={styles.editTensionBtn}
                    >
                      <span>
                        {isHybrid ? (
                          (!tensionOptionMain && !tensionOptionCross)
                            ? "Select String Tension (Required)"
                            : `Tension: Main [${
                                tensionOptionMain === "custom"
                                  ? `${tensionMain || "-"} ${tensionUnit}${prestretchEnabled ? ` (${prestretchPercent || "-"}% Prestretch)` : ""}`
                                  : tensionOptionMain === "standard"
                                  ? "Standard"
                                  : tensionOptionMain === "support"
                                  ? "Support"
                                  : "Not set"
                              }] / Cross [${
                                tensionOptionCross === "custom"
                                  ? `${tensionCross || "-"} ${tensionUnit}${prestretchEnabledCross ? ` (${prestretchPercentCross || "-"}% Prestretch)` : ""}`
                                  : tensionOptionCross === "standard"
                                  ? "Standard"
                                  : tensionOptionCross === "support"
                                  ? "Support"
                                  : "Not set"
                              }]`
                        ) : (
                          tensionOption === "custom"
                            ? `Tension: Custom (${tensionMain || '-'} ${tensionUnit} / ${tensionCross || '-'} ${tensionUnit})`
                            : tensionOption === "standard"
                            ? "Tension: Standard"
                            : tensionOption === "support"
                            ? "Tension: Talk with Support"
                            : "Select String Tension (Required)"
                        )}
                      </span>
                      <span style={{ fontSize: "1.2rem", lineHeight: "1" }}>&#x2192;</span>
                    </button>
                  </div>
                </>
              );
            })()}
            
            <div className={styles.editModalActions}>
              <div className={styles.editModalButtons}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="button" className={styles.saveButton} onClick={handleSaveEdit}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTensionModalOpen && (
        <div className={styles.modal} onClick={() => setIsTensionModalOpen(false)} style={{ zIndex: 10000 }}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {filteredBookings[selectedBookingIndex]?.isHybrid
                  ? tensionModalTarget === "cross"
                    ? "Configure Cross String Tension"
                    : "Configure Main String Tension"
                  : "Configure String Tension"}
              </h3>
              <button 
                className={styles.modalCloseIcon} 
                onClick={() => setIsTensionModalOpen(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>

            {/* Hybrid Sub-tabs */}
            {filteredBookings[selectedBookingIndex]?.isHybrid && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget !== "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget !== "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget !== "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("main")}
                >
                  1. Main String {tensionOptionMain ? `(${tensionOptionMain === "custom" ? (tensionMain ? `${tensionMain}${tensionUnit}` : "Custom") : tensionOptionMain === "standard" ? "Standard" : "Support"})` : ""}
                </button>
                <button
                  type="button"
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget === "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget === "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("cross")}
                >
                  2. Cross String {tensionOptionCross ? `(${tensionOptionCross === "custom" ? (tensionCross ? `${tensionCross}${tensionUnit}` : "Custom") : tensionOptionCross === "standard" ? "Standard" : "Support"})` : ""}
                </button>
              </div>
            )}
            
            {(() => {
              const isHybridEdit = !!filteredBookings[selectedBookingIndex]?.isHybrid;
              const activeTensionOption = isHybridEdit
                ? (tensionModalTarget === "cross" ? tensionOptionCross : tensionOptionMain)
                : tensionOption;

              const handleSelectTensionOption = (opt) => {
                if (isHybridEdit) {
                  if (tensionModalTarget === "cross") {
                    setTensionOptionCross(opt);
                  } else {
                    setTensionOptionMain(opt);
                  }
                } else {
                  setTensionOption(opt);
                }
              };

              return (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Select Option {isHybridEdit ? `(${tensionModalTarget === "cross" ? "Cross String" : "Main String"})` : ""}
                    </label>
                    <div className={styles.tensionOptions}>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "standard" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("standard")}
                      >
                        Standard
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "custom" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("custom")}
                      >
                        Custom
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "support" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("support")}
                      >
                        Talk with support
                      </button>
                    </div>
                  </div>



                  {activeTensionOption === "support" && (
                    <div className={styles.tensionSupport} style={{ marginTop: "16px" }}>
                      <p>
                        Our support team will help you choose the right tension {isHybridEdit ? `for ${tensionModalTarget === "cross" ? "Cross" : "Main"} String` : ""}.{" "}
                        <a href="tel:+971503469515" className={styles.tensionSupportLink}>
                          +971 503 469 515
                        </a>
                      </p>
                    </div>
                  )}

                  {activeTensionOption === "custom" && (
                    <div className={styles.tensionCustom}>
                      <div className={styles.unitToggle}>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "lbs" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("lbs")}
                        >
                          lbs
                        </button>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "kg" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("kg")}
                        >
                          kg
                        </button>
                      </div>

                      {isHybridEdit && tensionModalTarget === "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Main String Tension: <strong>{tensionOptionMain === "custom" ? `${tensionMain || "-"} ${tensionUnit}` : tensionOptionMain === "standard" ? "Standard" : tensionOptionMain === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}
                      {isHybridEdit && tensionModalTarget !== "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Cross String Tension: <strong>{tensionOptionCross === "custom" ? `${tensionCross || "-"} ${tensionUnit}` : tensionOptionCross === "standard" ? "Standard" : tensionOptionCross === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}

                      <div className={styles.tensionFields}>
                        {(!isHybridEdit || tensionModalTarget !== "cross") ? (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              {isHybridEdit ? "Main String Tension" : "Main Tension"}
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionMain}
                                onChange={(e) => setTensionMain(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 55"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              Cross String Tension
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionCross}
                                onChange={(e) => setTensionCross(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 53"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {isHybridEdit ? (
                        tensionModalTarget !== "cross" ? (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabled}
                                onChange={(e) => {
                                  setPrestretchEnabled(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercent("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Main Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercent}
                                onChange={(e) => setPrestretchPercent(e.target.value)}
                                disabled={!prestretchEnabled}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabled ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabled ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabledCross ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabledCross}
                                onChange={(e) => {
                                  setPrestretchEnabledCross(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercentCross("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Cross Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercentCross}
                                onChange={(e) => setPrestretchPercentCross(e.target.value)}
                                disabled={!prestretchEnabledCross}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabledCross ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabledCross ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        )
                      ) : (
                        <div className={styles.prestretchRow}>
                          <label
                            className={`${styles.prestretchLabel} ${
                              !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={prestretchEnabled}
                              onChange={(e) => {
                                setPrestretchEnabled(e.target.checked);
                                if (!e.target.checked) setPrestretchPercent("");
                              }}
                              className={styles.prestretchCheckbox}
                            />
                            Prestretch
                          </label>
                          <div className={styles.tensionInputRow}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={prestretchPercent}
                              onChange={(e) => setPrestretchPercent(e.target.value)}
                              disabled={!prestretchEnabled}
                              className={`${styles.tensionInput} ${
                                !prestretchEnabled ? styles.tensionInputDisabled : ""
                              }`}
                              placeholder="e.g. 10"
                            />
                            <span
                              className={`${styles.tensionUnitLabel} ${
                                !prestretchEnabled ? styles.tensionUnitDisabled : ""
                              }`}
                            >
                              %
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                    {isHybridEdit && tensionModalTarget !== "cross" && selectedStringIdCross && (
                      <button
                        type="button"
                        className={styles.closeModalButton}
                        style={{ background: "#f8fafc", color: "#0b7b43", border: "1px solid #0b7b43" }}
                        onClick={() => setTensionModalTarget("cross")}
                      >
                        Next: Cross Tension →
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.closeModalButton}
                      onClick={() => setIsTensionModalOpen(false)}
                    >
                      {isHybridEdit && tensionModalTarget !== "cross" && !selectedStringIdCross
                        ? "Save Main Tension"
                        : "Done / Close"}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {isAddressModalOpen && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <button
              type="button"
              onClick={() => setIsAddressModalOpen(false)}
              className={styles.closeModalButton}
            >
              ✖
            </button>
            <h3 className={styles.modalTitle}>Select or Add Address</h3>
            <AddressForm
              onSubmit={(addr) => {
                const formattedAddress = joinAddress(
                  addr.address_line,
                  addr.area,
                  addr.pincode,
                  addr.state,
                  addr.country
                );
                setAddressForCustomer(customerId, formattedAddress);
                setIsAddressModalOpen(false);
              }}
              customerId={customerId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingFinal;
